import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PiFilePdf, PiArrowSquareOut } from "react-icons/pi";
import AddBoltImage from '@/assets/visuals/add-bolt.webp';

import Dropzone from "@/components/common/Dropzone/Dropzone";
import FileScanner from "@/components/common/Dropzone/FileScanner";
import MotionText from "@/components/common/Motion/MotionText";
import MotionDiv from "@/components/common/Motion/MotionDiv";
import HeaderBack from "@/components/dashboard/Header/HeaderBack";
import ButtonLink from "@/components/common/Button/ButtonLink";

import documentsAPI from "@/services/api/document";
import receiptsAPI from "@/services/api/receipts";
import ProgressBar from "@/components/common/ProgressBar/ProgressBar";

import { useKycStore } from "@/store/useKycStore";
import { useBoltStore } from "@/store/useBoltStore";
import { useUserStore } from "@/store/useUserStore";

import BoltBlockedPage from "./BoltBlocked";
import { usePageSetup } from "@/hooks/usePageSetup";

export const BoltBalancePage = () => {
  const { t } = useTranslation()
  usePageSetup(t('bolt.pageTitle.balance'))
  const user = useUserStore((state) => state.user)

  if (user?.bolt_status === 'disabled') {
    return <BoltBlockedPage />
  }

  /* Reusing KYC */
  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  useEffect(() => setProgress(1), [])

  const navigate = useNavigate();

  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const analyzeFile = async (file: File) => {
    try {
      if (file.type === 'application/pdf') {
        const result = await documentsAPI.analyzeIncome(file);
        return result;
      } else if (file.type === 'image/jpeg' || file.type === 'image/png') {
        const result = await receiptsAPI.analyzeIncome(file);
        return result;
      } else {
        throw new Error(t('validation.unsupportedFileType'));
      }
    } catch (error) {
      setError(t('bolt.errors.recognitionFailed'));
      setIsScanning(false);
      return null;
    }
  };

  const setBalanceAnalysis = useBoltStore((state) => state.setBalanceAnalysis)

  const handleFileSelect = async (file: File) => {
    setIsScanning(true);
    setError(null);

    const analysis = await analyzeFile(file);

    if (analysis) {
      const date = new Date(analysis.reference.date);

      if (
        date.getFullYear() === new Date().getFullYear() &&
        date.getMonth() === new Date().getMonth()
      ) {
        setError(t('bolt.errors.currentMonthNotEnded'));
      } else {
        setBalanceAnalysis(analysis)
        navigate("/bolt/trips");
      }
    }
  };

  return (
    <div className="h-full bg-white flex flex-col">
      <HeaderBack>
        <div className="flex flex-row items-center w-[8.5rem] ml-[3.84rem]">
          <ProgressBar
            currentStep={progress}
            totalSteps={3}
          />
        </div>
      </HeaderBack>
      <div className="h-full flex flex-col px-5 pb-[10rem]">
        <MotionText
          as="h2"
          className="font-semibold text-3xl text-zinc-900 mb-3"
          delay={0.1}
        >
          {t('bolt.balance.title')}
        </MotionText>
        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-2"
          delay={0.2}
        >
          {t('bolt.balance.description')}
        </MotionText>
        <MotionText
          as="p"
          className="text-zinc-700 text-xs mb-8"
          delay={0.2}
        >
          <Link className="underline" to="/bolt/report/how">{t('bolt.balance.howToFind')}</Link>
        </MotionText>
        <MotionDiv
          className="bg-gray-100 p-4 rounded-lg flex flex-row gap-2 mb-4"
          delay={0.2}
        >
          <PiFilePdf className="h-6 w-6 text-black shrink-0" />
          <Link className="text-sm text-zinc-700 font-semibold flex-1 flex items-center gap-1" target="_blank" to="https://partners.bolt.eu/tax-reports">
            {t('bolt.balance.downloadPdf')} <span className="underline text-blue-600 hover:text-blue-700 inline-flex items-center gap-0.5">Bolt <PiArrowSquareOut className="w-4 h-4" /></span>
          </Link>
        </MotionDiv>
        {error && (
          <MotionText
            as="p"
            className="text-red-500 text-sm"
            delay={0.2}
          >
            {error}
          </MotionText>
        )}
        <MotionDiv
          className="flex flex-col gap-2"
          delay={0.3}
        >
          {isScanning ? (
            <FileScanner isScanning={isScanning} />
          ) : (
            <Dropzone
              onFileSelect={handleFileSelect}
              maxSize={10 * 1024 * 1024}
              buttonText={t('bolt.balance.selectFile')}
            >
              <img src={AddBoltImage}
                className='w-[4.375rem] h-[5.75rem] mb-2'
                alt="Add Bolt"
              />
            </Dropzone>
          )}
        </MotionDiv>
      </div>
      <motion.div className="px-5 pb-8 flex flex-col gap-2 absolute bottom-0 w-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.2,
          ease: "easeOut"
        }}
        whileTap={{ scale: 0.98 }}
      >
        <ButtonLink to={'/bolt/income'} variant='secondary'>
          {t('common.enterWithoutDocument')}
        </ButtonLink>
      </motion.div>
    </div>
  );
};

export default BoltBalancePage;